import maplibregl from 'maplibre-gl';
import {
  buildPmtilesUrl,
  GLOBAL_COORDINATES,
  STANDARD_NA_COORDINATES,
  markSingleImageUnavailable,
  isSingleImageMarkedUnavailable
} from '../config';
import WebGLForecastLayer from '../utils/WebGLForecastLayer';
import { QuadCoordinates } from '../utils/ImageBitmapCache';

export class LayerManager {
  private map: maplibregl.Map;
  private activeLayers: Set<string> = new Set();
  private sourceTiles: Map<string, string[]> = new Map();
  private webglLayers: Map<string, WebGLForecastLayer> = new Map();
  private pmtilesFallbackLayers: Set<string> = new Set();

  constructor(map: maplibregl.Map) {
    this.map = map;
  }

  public clearCache() {
    this.activeLayers.clear();
    this.sourceTiles.clear();
    this.webglLayers.clear();
    this.activeSlotMap.clear();
    this.bufferedTilesMap.clear();
  }

  private getFirstSymbolLayerId(): string | undefined {
    const style = this.map.getStyle();
    if (!style || !style.layers) return undefined;
    for (const layer of style.layers) {
      if (layer.type === 'symbol') {
        return layer.id;
      }
    }
    return undefined;
  }

  private resolveBeforeId(layerId: string, beforeId?: string): string | undefined {
    let targetBeforeId = beforeId;
    if (!targetBeforeId || !this.map.getLayer(targetBeforeId)) {
      targetBeforeId = this.getFirstSymbolLayerId();
    }

    if (layerId === 'live-weather-satellite') {
      if (this.map.getLayer('live-weather-temp-gradient')) {
        targetBeforeId = 'live-weather-temp-gradient';
      } else if (this.map.getLayer('live-weather-mrms-lightning')) {
        targetBeforeId = 'live-weather-mrms-lightning';
      }
    }
    return targetBeforeId && this.map.getLayer(targetBeforeId) ? targetBeforeId : undefined;
  }

  private resolveCoordinates(
    cleanTileUrl: string,
    coordinates?: QuadCoordinates
  ): QuadCoordinates {
    if (coordinates) return coordinates;
    const isGlobal = /_(?:gfs|ecmwf|gdps|icon13|icon|hgefs|dafs|gefs)(?:_|\.|$)/i.test(cleanTileUrl);
    return isGlobal ? GLOBAL_COORDINATES : STANDARD_NA_COORDINATES;
  }

  private isFallbackActive(layerOrBaseId: string, tileUrl?: string): boolean {
    const baseId = layerOrBaseId.replace(/-[01]$/, '');
    if (this.pmtilesFallbackLayers.has(layerOrBaseId) || this.pmtilesFallbackLayers.has(baseId)) {
      return true;
    }
    if (isSingleImageMarkedUnavailable(baseId)) {
      return true;
    }
    if (tileUrl) {
      const m = tileUrl.match(/\/forecast\/([^/]+)\//);
      if (m && m[1] && (this.pmtilesFallbackLayers.has(m[1]) || isSingleImageMarkedUnavailable(m[1]))) {
        return true;
      }
    }
    return false;
  }

  private markFallbackActive(baseLayerId: string, failedUrl?: string): void {
    const cleanBase = baseLayerId.replace(/-[01]$/, '');
    const strippedProduct = cleanBase.replace(/-(?:low|mid|high|total|surface|column|fill)$/, '');
    this.pmtilesFallbackLayers.add(baseLayerId);
    this.pmtilesFallbackLayers.add(cleanBase);
    this.pmtilesFallbackLayers.add(strippedProduct);
    markSingleImageUnavailable(cleanBase);
    markSingleImageUnavailable(strippedProduct);
    if (failedUrl) {
      const m = failedUrl.match(/\/forecast\/([^/]+)\//);
      if (m && m[1]) {
        this.pmtilesFallbackLayers.add(m[1]);
        markSingleImageUnavailable(m[1]);
      }
    }
  }

  private toPmtilesFallbackUrl(url: string): string {
    const clean = (url || '').split('?')[0];
    if (clean.endsWith('.webp') && !clean.includes('{z}')) {
      return buildPmtilesUrl(clean.replace(/_(?:1024|2048|4096)\.webp$/, '.pmtiles'));
    }
    return url;
  }

  /**
   * Hardware-accelerated WebGL dual-texture crossfade updater for single-image forecast layers.
   */
  public updateInterpolatedRasterLayer(
    baseLayerId: string,
    frame0Url: string,
    frame1Url: string,
    blend: number,
    opacity: number = 1.0,
    beforeId?: string,
    minzoom: number = 0,
    maxzoom: number = 5,
    coordinates?: QuadCoordinates
  ): void {
    if (!frame0Url) return;
    const cleanUrl0 = frame0Url.split('?')[0];
    const isSingleImage = cleanUrl0.endsWith('.webp') && !cleanUrl0.includes('{z}');
    const hasFallback = this.isFallbackActive(baseLayerId, cleanUrl0);

    // If not a single-image WebP or if this layer fell back to PMTiles
    if (!isSingleImage || hasFallback) {
      this.removeWebGLLayer(baseLayerId);
      const fallbackUrl = hasFallback && isSingleImage
        ? this.toPmtilesFallbackUrl(frame0Url)
        : frame0Url;
      this.updateBufferedRasterLayer(
        baseLayerId,
        [fallbackUrl],
        opacity,
        beforeId,
        minzoom,
        maxzoom,
        'linear',
        coordinates,
        true
      );
      return;
    }

    // Clean up any legacy raster sources/layers for this baseLayerId
    if (this.map.getLayer(baseLayerId) && !this.webglLayers.has(baseLayerId)) {
      this.removeLayer(baseLayerId);
    }
    if (this.map.getLayer(`${baseLayerId}-0`) || this.map.getLayer(`${baseLayerId}-1`)) {
      this.removeLayer(`${baseLayerId}-0`);
      this.removeLayer(`${baseLayerId}-1`);
      this.activeSlotMap.delete(baseLayerId);
    }

    const resolvedCoords = this.resolveCoordinates(cleanUrl0, coordinates);
    const safeBeforeId = this.resolveBeforeId(baseLayerId, beforeId);

    let customLayer = this.webglLayers.get(baseLayerId);
    if (!customLayer || !this.map.getLayer(baseLayerId)) {
      customLayer = new WebGLForecastLayer(
        baseLayerId,
        resolvedCoords,
        opacity,
        minzoom,
        maxzoom,
        (failedUrl: string) => {
          // Automatic fallback to PMTiles if single-image WebP is not on disk yet (404)
          this.markFallbackActive(baseLayerId, failedUrl);
          this.removeWebGLLayer(baseLayerId);
          const pmtilesUrl = this.toPmtilesFallbackUrl(failedUrl);
          this.updateBufferedRasterLayer(
            baseLayerId,
            [pmtilesUrl],
            opacity,
            beforeId,
            minzoom,
            maxzoom,
            'linear',
            resolvedCoords,
            true
          );
        }
      );
      this.webglLayers.set(baseLayerId, customLayer);
      try {
        this.map.addLayer(customLayer, safeBeforeId);
      } catch (_e) {
        try {
          this.map.addLayer(customLayer);
        } catch (_e2) {}
      }
    } else {
      customLayer.setZoomBounds(minzoom, maxzoom);
      if (safeBeforeId) {
        try {
          const layers = this.map.getStyle()?.layers;
          const curIdx = layers?.findIndex((l) => l.id === baseLayerId) ?? -1;
          const beforeIdx = layers?.findIndex((l) => l.id === safeBeforeId) ?? -1;
          if (curIdx !== -1 && beforeIdx !== -1 && curIdx > beforeIdx) {
            this.map.moveLayer(baseLayerId, safeBeforeId);
          }
        } catch (_e) {}
      }
    }

    this.bufferedTilesMap.set(baseLayerId, frame1Url && frame1Url !== frame0Url ? [frame0Url, frame1Url] : [frame0Url]);
    this.activeLayers.add(baseLayerId);
    customLayer.setFrames(frame0Url, frame1Url || frame0Url, blend, opacity, resolvedCoords);
  }

  private removeWebGLLayer(baseLayerId: string): void {
    if (this.webglLayers.has(baseLayerId)) {
      if (this.map && this.map.getLayer(baseLayerId)) {
        try {
          this.map.removeLayer(baseLayerId);
        } catch (_e) {}
      }
      this.webglLayers.delete(baseLayerId);
      this.activeLayers.delete(baseLayerId);
    }
  }

  public setLayerOpacity(baseLayerId: string, opacity: number): void {
    const webgl = this.webglLayers.get(baseLayerId);
    if (webgl) {
      webgl.setOpacity(opacity);
    }
    const currentSlot = this.activeSlotMap.get(baseLayerId) ?? 0;
    const currentLayerId = `${baseLayerId}-${currentSlot}`;
    if (this.map.getLayer(currentLayerId)) {
      this.map.setPaintProperty(currentLayerId, 'raster-opacity', opacity);
    }
    if (this.map.getLayer(baseLayerId) && !this.webglLayers.has(baseLayerId)) {
      this.map.setPaintProperty(baseLayerId, 'raster-opacity', opacity);
    }
  }

  /**
   * Sets up or updates a standard MapLibre raster/image layer dynamically.
   */
  public updateRasterLayer(
    layerId: string,
    tiles: string[],
    opacity: number = 1.0,
    beforeId?: string,
    minzoom: number = 0,
    maxzoom: number = 5,
    resampling: 'nearest' | 'linear' = 'linear',
    coordinates?: QuadCoordinates
  ) {
    let resolvedTiles = tiles;
    let firstTile = resolvedTiles[0] || '';
    let cleanTileUrl = firstTile.split('?')[0];
    let isSingleImage = cleanTileUrl.endsWith('.webp') && !cleanTileUrl.includes('{z}');
    const hasFallback = this.isFallbackActive(layerId, cleanTileUrl);

    if (isSingleImage && hasFallback) {
      resolvedTiles = [this.toPmtilesFallbackUrl(firstTile)];
      firstTile = resolvedTiles[0] || '';
      cleanTileUrl = firstTile.split('?')[0];
      isSingleImage = false;
    }

    if (
      isSingleImage &&
      !hasFallback &&
      !/-[01]$/.test(layerId)
    ) {
      this.updateInterpolatedRasterLayer(
        layerId,
        firstTile,
        resolvedTiles[1] || firstTile,
        0.0,
        opacity,
        beforeId,
        minzoom,
        maxzoom,
        coordinates
      );
      return;
    }

    const sourceId = `${layerId}-source`;
    const existingTiles = this.sourceTiles.get(sourceId) || [];
    const tilesChanged =
      existingTiles.length !== resolvedTiles.length || existingTiles.some((t, i) => t !== resolvedTiles[i]);

    const isPmtiles = cleanTileUrl.startsWith('pmtiles://') || cleanTileUrl.includes('.pmtiles');
    const cleanPmtilesUrl = isPmtiles ? buildPmtilesUrl(firstTile.replace(/\/{z}\/{x}\/{y}.*$/, '')) : '';
    const resolvedCoords = this.resolveCoordinates(cleanTileUrl, coordinates);

    const sourceConfig: any = isPmtiles
      ? {
          type: 'raster',
          url: cleanPmtilesUrl,
          tileSize: 256,
          minzoom: minzoom
        }
      : isSingleImage
      ? {
          type: 'image',
          url: resolvedTiles[0],
          coordinates: resolvedCoords
        }
      : {
          type: 'raster',
          tileSize: 256,
          minzoom: minzoom,
          maxzoom: maxzoom,
          tiles: [
            resolvedTiles[0].includes('/{z}/{x}/{y}')
              ? resolvedTiles[0]
              : `${resolvedTiles[0].replace(/\/$/, '')}/{z}/{x}/{y}.webp`
          ]
        };

    const safeBeforeId = this.resolveBeforeId(layerId, beforeId);

    if (this.map.getSource(sourceId)) {
      if (tilesChanged) {
        const source = this.map.getSource(sourceId) as any;
        let updated = false;
        if (
          isSingleImage &&
          source &&
          source.type === 'image' &&
          typeof source.updateImage === 'function'
        ) {
          try {
            source.updateImage({
              url: resolvedTiles[0],
              coordinates: resolvedCoords
            });
            updated = true;
          } catch (_e) {
            updated = false;
          }
        } else if (
          !isPmtiles &&
          !isSingleImage &&
          source &&
          source.type === 'raster' &&
          typeof source.setTiles === 'function'
        ) {
          try {
            source.setTiles(resolvedTiles);
            updated = true;
          } catch (_e) {
            updated = false;
          }
        }

        if (updated) {
          this.sourceTiles.set(sourceId, [...resolvedTiles]);
          if (this.map.getLayer(layerId)) {
            this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
            this.map.setPaintProperty(layerId, 'raster-resampling', resampling);
            try {
              this.map.moveLayer(layerId, safeBeforeId);
            } catch (_e) {}
          }
        } else {
          if (this.map.getLayer(layerId)) {
            this.map.removeLayer(layerId);
          }
          this.map.removeSource(sourceId);

          this.map.addSource(sourceId, sourceConfig);
          this.sourceTiles.set(sourceId, [...resolvedTiles]);

          this.map.addLayer(
            {
              id: layerId,
              type: 'raster',
              source: sourceId,
              paint: {
                'raster-opacity': opacity,
                'raster-fade-duration': 0,
                'raster-resampling': resampling
              }
            },
            safeBeforeId
          );

          this.triggerRepaintSequence();
        }
      } else {
        if (this.map.getLayer(layerId)) {
          this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
          this.map.setPaintProperty(layerId, 'raster-resampling', resampling);
          try {
            this.map.moveLayer(layerId, safeBeforeId);
          } catch (_e) {}
        }
      }
    } else {
      this.map.addSource(sourceId, sourceConfig);
      this.sourceTiles.set(sourceId, [...resolvedTiles]);

      this.map.addLayer(
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': opacity,
            'raster-fade-duration': 0,
            'raster-resampling': resampling
          }
        },
        safeBeforeId
      );

      this.triggerRepaintSequence();
    }
    this.activeLayers.add(layerId);
  }

  private triggerRepaintSequence() {
    if (!this.map) return;
    try {
      this.map.triggerRepaint();
    } catch (_e) {}
    requestAnimationFrame(() => {
      if (this.map) {
        try {
          this.map.triggerRepaint();
        } catch (_e) {}
      }
    });
  }

  /**
   * Removes a layer and its source cleanly.
   */
  public removeLayer(layerId: string) {
    if (this.webglLayers.has(layerId)) {
      this.removeWebGLLayer(layerId);
    }

    const sourceId = `${layerId}-source`;

    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId);
    }

    this.activeLayers.delete(layerId);
    this.sourceTiles.delete(sourceId);
  }

  private activeSlotMap: Map<string, number> = new Map();
  private bufferedTilesMap: Map<string, string[]> = new Map();

  /**
   * Double-buffered or WebGL-interpolated raster layer updater to eliminate flickering during playback.
   */
  public updateBufferedRasterLayer(
    baseLayerId: string,
    tiles: string[],
    opacity: number = 1.0,
    beforeId?: string,
    minzoom: number = 0,
    maxzoom: number = 5,
    resampling: 'nearest' | 'linear' = 'linear',
    coordinates?: QuadCoordinates,
    skipWebGL: boolean = false
  ) {
    let resolvedTiles = tiles;
    let firstTile = resolvedTiles[0] || '';
    let cleanTileUrl = firstTile.split('?')[0];
    let isSingleImage = cleanTileUrl.endsWith('.webp') && !cleanTileUrl.includes('{z}');
    const hasFallback = this.isFallbackActive(baseLayerId, cleanTileUrl);

    if (isSingleImage && hasFallback) {
      resolvedTiles = [this.toPmtilesFallbackUrl(firstTile)];
      firstTile = resolvedTiles[0] || '';
      cleanTileUrl = firstTile.split('?')[0];
      isSingleImage = false;
    }

    if (
      isSingleImage &&
      !skipWebGL &&
      !hasFallback
    ) {
      this.updateInterpolatedRasterLayer(
        baseLayerId,
        firstTile,
        resolvedTiles[1] || firstTile,
        0.0,
        opacity,
        beforeId,
        minzoom,
        maxzoom,
        coordinates
      );
      return;
    }

    // If transitioning from WebGL custom layer to PMTiles/Raster (e.g. high-zoom hrtemp or terrain mode)
    if (this.webglLayers.has(baseLayerId)) {
      this.removeWebGLLayer(baseLayerId);
    }

    const prevTiles = this.bufferedTilesMap.get(baseLayerId) || [];
    const tilesChanged =
      prevTiles.length !== resolvedTiles.length || prevTiles.some((t, i) => t !== resolvedTiles[i]);

    const currentSlot = this.activeSlotMap.get(baseLayerId) || 0;
    const currentLayerId = `${baseLayerId}-${currentSlot}`;

    if (!tilesChanged && (this.map.getLayer(currentLayerId) || this.map.getLayer(baseLayerId))) {
      const activeId = this.map.getLayer(currentLayerId) ? currentLayerId : baseLayerId;
      this.updateRasterLayer(activeId, resolvedTiles, opacity, beforeId, minzoom, maxzoom, resampling, coordinates);
      return;
    }

    this.bufferedTilesMap.set(baseLayerId, [...resolvedTiles]);
    const nextSlot = 1 - currentSlot;
    const nextLayerId = `${baseLayerId}-${nextSlot}`;

    // Update next slot with new tiles (works for both PMTiles and Image/Raster sources without blank flashes)
    this.updateRasterLayer(nextLayerId, resolvedTiles, opacity, beforeId, minzoom, maxzoom, resampling, coordinates);

    if (this.map.getLayer(nextLayerId)) {
      this.map.setPaintProperty(nextLayerId, 'raster-opacity', opacity);
    }

    const prevLayerId = currentLayerId;
    const nextSourceId = `${nextLayerId}-source`;
    let cleanedUp = false;
    const hidePrevSlot = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (this.map && typeof (this.map as any).off === 'function') {
        try {
          (this.map as any).off('sourcedata', onSourceData);
        } catch (_e) {}
      }
      if (this.map && this.map.getLayer(prevLayerId)) {
        if (this.activeSlotMap.get(baseLayerId) === nextSlot) {
          this.map.setPaintProperty(prevLayerId, 'raster-opacity', 0);
        }
      }
    };
    const onSourceData = (e: any) => {
      if (e && e.sourceId === nextSourceId && e.isSourceLoaded) {
        hidePrevSlot();
      }
    };
    if (this.map && typeof (this.map as any).on === 'function') {
      try {
        (this.map as any).on('sourcedata', onSourceData);
      } catch (_e) {}
    }
    setTimeout(hidePrevSlot, 650);

    this.activeSlotMap.set(baseLayerId, nextSlot);
    this.triggerRepaintSequence();
  }

  public removeBufferedRasterLayer(baseLayerId: string) {
    this.removeWebGLLayer(baseLayerId);
    this.removeLayer(`${baseLayerId}-0`);
    this.removeLayer(`${baseLayerId}-1`);
    this.removeLayer(baseLayerId);
    this.activeSlotMap.delete(baseLayerId);
    this.bufferedTilesMap.delete(baseLayerId);
  }

  /**
   * Safe check for layer visibility.
   */
  public hasLayer(layerId: string): boolean {
    return (
      this.webglLayers.has(layerId) ||
      this.activeLayers.has(layerId) ||
      this.activeLayers.has(`${layerId}-0`) ||
      this.activeLayers.has(`${layerId}-1`)
    );
  }

  /**
   * Retrieves active tile URL array for a given base layer ID.
   */
  public getActiveSourceTiles(baseLayerId: string): string[] | undefined {
    return (
      this.bufferedTilesMap.get(baseLayerId) ||
      this.sourceTiles.get(`${baseLayerId}-source`) ||
      this.sourceTiles.get(`${baseLayerId}-0-source`) ||
      this.sourceTiles.get(`${baseLayerId}-1-source`)
    );
  }
}
export default LayerManager;
